import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { formatDateForExcel, formatInternationalNumber, getInternationalCsvSeparator } from 'src/app/shared/constants/localization.constant';
import { MarketFlip } from 'src/app/shared/models/flipper.model';
import { FlipperStateService } from '../flipper-state.service';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { UpgradeResource } from 'src/app/shared/models/items.model';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { SALES_TAXES } from 'src/app/shared/constants/market.constant';
import { Subscription } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatOptionModule } from '@angular/material/core';

@Component({
  selector: 'app-flipper-session-flips',
  imports: [CommonModule, MatPaginatorModule, MatSortModule, MatButtonModule, SmallNumberPipe, MatExpansionModule, MatTooltipModule, MatIconModule, MatTableModule, CopyTextButtonComponent, CheckPriceButtonComponent],
  templateUrl: './flipper-session-flips.component.html',
  styleUrl: '../flipper.component.scss',
})
export class FlipperSessionFlipsComponent implements AfterViewInit, OnDestroy {

  @ViewChild('consumedFlipsSort') set consumedFlipsSortSetter(sort: MatSort) {
    this.flipperStateService.consumedFlipsSort = sort;
    this.flipperStateService.setupConsumedFlipsDataSource();
  }

  @ViewChild('consumedFlipsPaginator') set consumedFlipsPaginatorSetter(paginator: MatPaginator) {
    this.flipperStateService.consumedFlipsPaginator = paginator;
  }


  subscriptions: Subscription[] = [];

  get consumedFlipsDisplayedColumns(): string[] {
    if (this.flipperStateService.flipperFormGroup.controls.showNonPremium.value) {
      return [
        'itemUsCompleteName',
        'consumedAt',
        'sellOrderUnitPriceSilver',
        'buyOrderUnitPriceSilver',
        'upgrade',
        'upgradeCost',
        'profitPremium',
        'profitPercentagePremium',
        'profitNonPremium',
        'profitPercentageNonPremium',
        'consumeFlip',
      ];
    } else {
      return [
        'itemUsCompleteName',
        'consumedAt',
        'sellOrderUnitPriceSilver',
        'buyOrderUnitPriceSilver',
        'upgrade',
        'upgradeCost',
        'profitPremium',
        'profitPercentagePremium',
        'consumeFlip',
      ];

    }
  }

  // MARK: CONSTRUCTOR
  constructor(public userSettingsService: UserSettingsService, public imageService: AlbionImageService, public flipperStateService: FlipperStateService,
    private matDialog: MatDialog,) { }

  // MARK: LIFETIME
  ngAfterViewInit() {
    this.flipperStateService.setupConsumedFlipsDataSource();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // MARK: Totals
  getConsumedFlipsTotalProfitPremium(): number {
    return this.userSettingsService.getUserConsumedFlips().reduce((acc, flip) => acc + flip.profitPremium, 0);
  }

  getConsumedFlipsTotalProfitPercentagePremium(): number {
    return this.getConsumedFlipsTotalProfitPremium() / (this.getConsumedFlipsTotalBuy() + this.getConsumedFlipsTotalUpgradeCost());
  }

  getConsumedFlipsTotalProfitNonPremium(): number {
    return this.userSettingsService.getUserConsumedFlips().reduce((acc, flip) => acc + flip.profitNonPremium, 0);
  }

  getConsumedFlipsTotalProfitPercentageNonPremium(): number {
    return this.getConsumedFlipsTotalProfitNonPremium() / (this.getConsumedFlipsTotalBuy() + this.getConsumedFlipsTotalUpgradeCost());
  }

  getConsumedFlipsTotalUpgradeCost(): number {
    return this.userSettingsService.getUserConsumedFlips().reduce((acc, flip) => acc + flip.upgradeCost, 0);
  }

  getConsumedFlipsTotalBuy(): number {
    return this.userSettingsService.getUserConsumedFlips().reduce((acc, flip) => acc + flip.sellOrder.unitPriceSilver, 0);
  }

  getConsumedFlipsTotalSell(): number {
    return this.userSettingsService.getUserConsumedFlips().reduce((acc, flip) => acc + flip.buyOrder.unitPriceSilver * (1 - SALES_TAXES.premium.value), 0);
  }

  // MARK: Remove Flips
  removeConsumedFlip(flip: MarketFlip) {
    this.userSettingsService.removeUserConsumedFlip(flip);
    this.flipperStateService.setupConsumedFlipsDataSource();
  }

  removeAllConsumedFlips() {
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Delete', message: `Are you sure you want to delete all the consumed flips in this session? This can't be undone.` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.userSettingsService.getUserConsumedFlips().forEach((flip) => {
          this.userSettingsService.removeUserConsumedFlip(flip);
        });
        this.flipperStateService.setupConsumedFlipsDataSource();
      }
    });
  }

  // MARK: getConsumedFlipsRunesDemand
  getConsumedFlipsRunesDemand(): UpgradeResource[] {
    return Object.values(
      this.userSettingsService
        .getUserConsumedFlips()
        .flatMap((flip) => flip.upgradeResources)
        .reduce(
          (acc, curr) => {
            if (acc[curr.uniqueName]) {
              acc[curr.uniqueName].count += curr.count;
            } else {
              acc[curr.uniqueName] = { ...curr };
            }
            return acc;
          },
          {} as { [uniqueName: string]: UpgradeResource }
        )
    );
  }
}
